import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Plus, X, Trash2, Copy as CopyIcon, ExternalLink, Calendar, Link2 } from "lucide-react";
import { toast } from "sonner";

const DAYS = [
  { key: "monday", label: "Lun" },
  { key: "tuesday", label: "Mar" },
  { key: "wednesday", label: "Mié" },
  { key: "thursday", label: "Jue" },
  { key: "friday", label: "Vie" },
  { key: "saturday", label: "Sáb" },
  { key: "sunday", label: "Dom" },
];

const Booking = () => {
  const [pages, setPages] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("pages");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/booking/pages"), api.get("/booking/bookings")])
      .then(([p, b]) => { setPages(p.data); setBookings(b.data); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <Layout
      title="Agendamiento Público"
      subtitle="Links tipo Calendly para tu equipo"
      actions={
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-primary flex items-center gap-2" data-testid="new-page-btn">
          <Plus size={14} /> Nuevo link
        </button>
      }
    >
      <div className="flex gap-2 mb-5 border-b border-[#E5E5E5]">
        <button onClick={() => setTab("pages")} data-testid="tab-pages" className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px ${tab === "pages" ? "border-[#D99D26] text-[#0A0A0A]" : "border-transparent text-[#666]"}`}>
          Mis links ({pages.length})
        </button>
        <button onClick={() => setTab("bookings")} data-testid="tab-bookings" className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px ${tab === "bookings" ? "border-[#D99D26] text-[#0A0A0A]" : "border-transparent text-[#666]"}`}>
          Reservas recibidas ({bookings.length})
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><span className="spinner" /></div>
      ) : tab === "pages" ? (
        pages.length === 0 ? (
          <div className="aura-card-flat p-12 text-center">
            <div className="w-14 h-14 rounded-2xl bg-[#D99D26]/15 text-[#8a5e10] flex items-center justify-center mx-auto mb-4">
              <Link2 size={24} />
            </div>
            <h3 className="font-display text-2xl font-medium">Sin links de agendamiento</h3>
            <p className="text-[#666] mt-2 mb-5">Crea un link público para que cualquiera agende contigo sin login.</p>
            <button onClick={() => setShowForm(true)} className="btn-primary">Crear primer link</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="pages-grid">
            {pages.map((p) => <PageCard key={p.id} page={p} onEdit={() => { setEditing(p); setShowForm(true); }} onChange={load} />)}
          </div>
        )
      ) : (
        <BookingsList items={bookings} onChange={load} />
      )}

      {showForm && <PageForm initial={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); load(); }} />}
    </Layout>
  );
};

const PageCard = ({ page, onEdit, onChange }) => {
  const publicUrl = `${window.location.origin}/agendar/${page.slug}`;
  const copyUrl = () => { navigator.clipboard.writeText(publicUrl); toast.success("Link copiado"); };
  const remove = async () => {
    if (!window.confirm("¿Eliminar este link?")) return;
    await api.delete(`/booking/pages/${page.id}`);
    toast.success("Eliminado");
    onChange();
  };
  return (
    <div className="aura-card p-5 group" data-testid={`page-${page.slug}`}>
      <div className="flex justify-between items-start mb-2">
        <div className="w-3 h-3 rounded-full" style={{ background: page.color || "#D99D26" }} />
        <span className={`pill ${page.active ? "pill-green" : "pill-gray"} text-[10px]`}>{page.active ? "Activo" : "Pausado"}</span>
      </div>
      <h3 className="font-display text-lg font-medium">{page.title}</h3>
      <div className="text-xs text-[#666] mt-1">{page.duration_minutes} min · {page.timezone_label}</div>
      <div className="mt-3 p-2.5 rounded-lg bg-[#F0F0F0] flex items-center gap-2">
        <code className="text-xs flex-1 truncate text-[#0A0A0A] font-mono">/agendar/{page.slug}</code>
        <button onClick={copyUrl} className="text-[#666] hover:text-[#D99D26]" data-testid={`copy-${page.slug}`}><CopyIcon size={13} /></button>
        <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="text-[#666] hover:text-[#D99D26]"><ExternalLink size={13} /></a>
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={onEdit} className="btn-ghost text-xs">Editar</button>
        <button onClick={remove} className="text-[#666] hover:text-red-600 ml-auto opacity-0 group-hover:opacity-100"><Trash2 size={13} /></button>
      </div>
    </div>
  );
};

const BookingsList = ({ items, onChange }) => {
  const cancel = async (id) => {
    if (!window.confirm("¿Cancelar reserva? Le avisaremos al cliente por email.")) return;
    await api.patch(`/booking/bookings/${id}/cancel`);
    toast.success("Cancelada");
    onChange();
  };
  if (items.length === 0) {
    return (
      <div className="aura-card-flat p-12 text-center">
        <Calendar size={36} className="text-[#666] mx-auto mb-3" />
        <h3 className="font-display text-xl font-medium">Sin reservas aún</h3>
        <p className="text-[#666] text-sm mt-2">Comparte tu link público y empezarás a recibir reservas.</p>
      </div>
    );
  }
  return (
    <div className="aura-card-flat p-2 overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-[#E5E5E5] text-left text-xs uppercase tracking-wider text-[#666]">
            <th className="p-3">Cuándo</th>
            <th className="p-3">Cliente</th>
            <th className="p-3">Tema</th>
            <th className="p-3">Contacto</th>
            <th className="p-3">Estado</th>
            <th className="p-3"></th>
          </tr>
        </thead>
        <tbody data-testid="bookings-table">
          {items.map((b) => (
            <tr key={b.id} className="border-b border-[#F0F0F0]" data-testid={`booking-${b.id}`}>
              <td className="p-3 text-sm">
                <div className="font-medium">{new Date(b.slot_datetime).toLocaleString("es-MX", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                <div className="text-xs text-[#666]">{b.duration_minutes}min</div>
              </td>
              <td className="p-3 text-sm font-medium">{b.attendee_name}</td>
              <td className="p-3 text-sm">{b.title}</td>
              <td className="p-3 text-sm">
                <div>{b.attendee_email}</div>
                {b.attendee_phone && <div className="text-xs text-[#666]">{b.attendee_phone}</div>}
              </td>
              <td className="p-3"><span className={`pill ${b.status === "confirmada" ? "pill-green" : "pill-red"} text-[10px]`}>{b.status}</span></td>
              <td className="p-3">
                {b.status === "confirmada" && (
                  <button onClick={() => cancel(b.id)} className="text-xs text-red-600 hover:underline">Cancelar</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PageForm = ({ initial, onClose, onSaved }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(initial || {
    slug: "",
    title: "Reunión con Rubí",
    description: "",
    duration_minutes: 30,
    buffer_minutes: 15,
    color: "#D99D26",
    timezone: "America/Mexico_City",
    timezone_label: "Hora CDMX (UTC-6)",
    days_ahead: 30,
    active: true,
    meet_link_template: "",
    location_note: "",
    working_hours: DAYS.reduce((acc, d) => {
      acc[d.key] = { enabled: !["saturday", "sunday"].includes(d.key), start: "09:00", end: "18:00" };
      return acc;
    }, {}),
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.slug || !form.title) return;
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/booking/pages/${initial.id}`, form);
        toast.success("Link actualizado");
      } else {
        await api.post("/booking/pages", form);
        toast.success("Link creado");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const toggleDay = (day) => {
    setForm((f) => ({
      ...f,
      working_hours: {
        ...f.working_hours,
        [day]: { ...f.working_hours[day], enabled: !f.working_hours[day]?.enabled },
      },
    }));
  };
  const updateHours = (day, field, value) => {
    setForm((f) => ({
      ...f,
      working_hours: {
        ...f.working_hours,
        [day]: { ...f.working_hours[day], [field]: value },
      },
    }));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-7 space-y-4">
        <div className="flex justify-between items-start">
          <div>
            <div className="label-overline">{isEdit ? "Editar" : "Crear"}</div>
            <h2 className="font-display text-2xl font-medium">{isEdit ? "Editar link" : "Nuevo link de agendamiento"}</h2>
          </div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>

        <div>
          <label className="aura-label">Slug público *</label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#666] font-mono">/agendar/</span>
            <input required className="aura-input flex-1" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} placeholder="benjamin" disabled={isEdit} data-testid="page-slug" />
          </div>
        </div>
        <div>
          <label className="aura-label">Título *</label>
          <input required className="aura-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="30 min con Benjamín" data-testid="page-title" />
        </div>
        <div>
          <label className="aura-label">Descripción</label>
          <textarea className="aura-input" rows="2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="aura-label">Duración (min)</label>
            <select className="aura-input" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) })}>
              {[15, 30, 45, 60, 90].map((v) => <option key={v} value={v}>{v} min</option>)}
            </select>
          </div>
          <div>
            <label className="aura-label">Buffer (min)</label>
            <select className="aura-input" value={form.buffer_minutes} onChange={(e) => setForm({ ...form, buffer_minutes: parseInt(e.target.value) })}>
              {[0, 5, 10, 15, 30].map((v) => <option key={v} value={v}>{v} min</option>)}
            </select>
          </div>
          <div>
            <label className="aura-label">Días adelante</label>
            <input type="number" className="aura-input" value={form.days_ahead} onChange={(e) => setForm({ ...form, days_ahead: parseInt(e.target.value) || 30 })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="aura-label">Link Google Meet (opcional)</label>
            <input className="aura-input" value={form.meet_link_template} onChange={(e) => setForm({ ...form, meet_link_template: e.target.value })} placeholder="https://meet.google.com/..." />
          </div>
          <div>
            <label className="aura-label">Notas de ubicación</label>
            <input className="aura-input" value={form.location_note} onChange={(e) => setForm({ ...form, location_note: e.target.value })} placeholder="Llamada por Google Meet" />
          </div>
        </div>
        <div>
          <label className="aura-label">Horario disponible</label>
          <div className="space-y-2">
            {DAYS.map((d) => {
              const cfg = form.working_hours?.[d.key] || { enabled: false, start: "09:00", end: "18:00" };
              return (
                <div key={d.key} className="flex items-center gap-3 p-2 rounded-lg border border-[#E5E5E5]">
                  <label className="flex items-center gap-2 w-16">
                    <input type="checkbox" checked={cfg.enabled} onChange={() => toggleDay(d.key)} className="accent-[#D99D26]" />
                    <span className="text-sm font-medium">{d.label}</span>
                  </label>
                  <input type="time" className="aura-input text-sm py-1 flex-1" value={cfg.start} onChange={(e) => updateHours(d.key, "start", e.target.value)} disabled={!cfg.enabled} />
                  <span className="text-[#666]">→</span>
                  <input type="time" className="aura-input text-sm py-1 flex-1" value={cfg.end} onChange={(e) => updateHours(d.key, "end", e.target.value)} disabled={!cfg.enabled} />
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input type="checkbox" id="active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#D99D26]" />
          <label htmlFor="active" className="text-sm">Página activa (recibir nuevas reservas)</label>
        </div>

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost">Cancelar</button>
          <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center flex items-center gap-2" data-testid="page-submit">
            {saving && <span className="spinner" />} {isEdit ? "Guardar cambios" : "Crear link"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Booking;
